import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import { auth } from '@/api/driver'
import { Logo } from '@/components/Shell'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/form'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { errorText } from '@/lib/utils'

/** Only same-site paths, so a crafted link can't send people elsewhere after sign-in. */
function useNext() {
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/'
  return next.startsWith('/') && !next.startsWith('//') ? next : '/'
}

function AuthFrame({ title, sub, children, footer }: { title: string; sub?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col px-4 py-6">
        <Logo />
        <div className="flex flex-1 flex-col justify-center py-8">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {sub && <p className="mt-1 text-sm text-text-2">{sub}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="text-center text-sm text-text-2">{footer}</div>}
      </div>
    </div>
  )
}

function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input {...props} type={show ? 'text' : 'password'} className="h-11 pr-10" />
      <button type="button" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow(!show)} className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center text-muted">
        {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
}

export function LoginPage() {
  const { t } = useI18n()
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const next = useNext()
  const [id, setId] = useState('')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (user) return <Navigate to={next} replace />
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(id, pw)
      navigate(next, { replace: true })
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <AuthFrame
      title={t('Sign in')}
      sub={t('Use the same account as the Saadhan Charge app.')}
      footer={<>{t('New here?')} <Link to={`/signup${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-text underline">{t('Create an account')}</Link></>}
    >
      <form onSubmit={submit} className="grid gap-4">
        <Field label={t('Phone or email')}>{(p) => <Input {...p} className="h-11" value={id} onChange={(e) => setId(e.target.value)} autoComplete="username" inputMode="email" autoFocus />}</Field>
        <Field label={t('Password')}>{(p) => <PasswordInput {...p} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" />}</Field>
        {error && <p role="alert" className="rounded-lg bg-danger-soft p-2.5 text-sm text-danger">{error}</p>}
        <Button type="submit" size="lg" className="h-11" loading={busy} disabled={!id.trim() || !pw}>{t('Sign in')}</Button>
        <Link to="/forgot" className="text-center text-sm text-text-2 underline-offset-4 hover:underline">{t('Forgot your password?')}</Link>
      </form>
    </AuthFrame>
  )
}

/** The API's rule: 8+ characters with upper and lower case, a digit and a symbol. */
export function passwordProblems(pw: string) {
  const p: string[] = []
  if (pw.length < 8) p.push('at least 8 characters')
  if (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw)) p.push('upper and lower case letters')
  if (!/\d/.test(pw)) p.push('a number')
  if (!/[^A-Za-z0-9]/.test(pw)) p.push('a symbol')
  return p
}

/** Nepali mobile numbers: 98XXXXXXXX or 97XXXXXXXX, with or without +977. */
export function normalPhone(raw: string) {
  const d = raw.replace(/\D/g, '').replace(/^977/, '').replace(/^0/, '')
  return /^9\d{9}$/.test(d) ? d : null
}

export function SignupPage() {
  const { t } = useI18n()
  const { user, signIn } = useAuth()
  const navigate = useNavigate()
  const next = useNext()
  const [step, setStep] = useState<'phone' | 'code' | 'details'>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [token, setToken] = useState('')
  const [name, setName] = useState('')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [masked, setMasked] = useState('')
  if (user && step !== 'details') return <Navigate to={next} replace />

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }
  const num = normalPhone(phone)
  const problems = passwordProblems(pw)

  const sendCode = (e?: FormEvent) => {
    e?.preventDefault()
    if (!num) return
    run(async () => {
      const r = await auth.sendOtp(num)
      setMasked(r.phone)
      setStep('code')
    })
  }

  return (
    <AuthFrame
      title={step === 'phone' ? t('Create an account') : step === 'code' ? t('Enter the code') : t('Almost done')}
      sub={step === 'phone' ? t('Charge at any Saadhan Charge station. We text you a code to check your number.') : step === 'code' ? t('We sent a 6-digit code to {phone}.', { phone: masked || num || phone }) : t('Choose a password for signing in.')}
      footer={<>{t('Already have an account?')} <Link to="/login" className="font-medium text-text underline">{t('Sign in')}</Link></>}
    >
      {step === 'phone' && (
        <form onSubmit={sendCode} className="grid gap-4">
          <Field label={t('Mobile number')} hint={t('Nepal numbers only for now.')}>
            {(p) => (
              <div className="flex">
                <span className="flex h-11 items-center rounded-l-lg border border-r-0 border-border bg-surface-2 px-3 text-sm text-text-2">+977</span>
                <Input {...p} className="h-11 rounded-l-none" inputMode="tel" autoComplete="tel-national" placeholder="98XXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus />
              </div>
            )}
          </Field>
          {error && <p role="alert" className="rounded-lg bg-danger-soft p-2.5 text-sm text-danger">{error}</p>}
          <Button type="submit" size="lg" className="h-11" loading={busy} disabled={!num}>{t('Send code')}</Button>
          <p className="text-xs text-text-2">
            {t('By continuing you agree to the')} <a className="underline" href="https://saadhan.app/terms" target="_blank" rel="noreferrer">{t('terms')}</a> {t('and')} <a className="underline" href="https://saadhan.app/privacy" target="_blank" rel="noreferrer">{t('privacy policy')}</a>.
          </p>
        </form>
      )}
      {step === 'code' && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            run(async () => {
              const r = await auth.verifyOtp(num!, code.trim())
              setToken(r.verification_token)
              setStep('details')
            })
          }}
          className="grid gap-4"
        >
          <Field label={t('Code')}>{(p) => <Input {...p} className="h-12 text-center font-mono text-xl tracking-[0.4em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />}</Field>
          {error && <p role="alert" className="rounded-lg bg-danger-soft p-2.5 text-sm text-danger">{error}</p>}
          <Button type="submit" size="lg" className="h-11" loading={busy} disabled={code.length < 4}>{t('Continue')}</Button>
          <div className="flex justify-between text-sm">
            <button type="button" className="flex items-center gap-1 text-text-2" onClick={() => { setStep('phone'); setCode(''); setError(null) }}><ArrowLeft className="size-4" /> {t('Change number')}</button>
            <button type="button" className="text-text-2 underline" disabled={busy} onClick={() => sendCode()}>{t('Send again')}</button>
          </div>
        </form>
      )}
      {step === 'details' && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            run(async () => {
              const r = await auth.register({ verification_token: token, password: pw, full_name: name.trim() || undefined })
              signIn(r)
              navigate(next, { replace: true })
            })
          }}
          className="grid gap-4"
        >
          <Field label={t('Your name')}>{(p) => <Input {...p} className="h-11" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" autoFocus />}</Field>
          <Field label={t('Password')} hint={pw && problems.length ? t('Needs {list}.', { list: problems.map((x) => t(x)).join(', ') }) : t('8+ characters with upper and lower case, a number and a symbol.')}>
            {(p) => <PasswordInput {...p} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />}
          </Field>
          {error && <p role="alert" className="rounded-lg bg-danger-soft p-2.5 text-sm text-danger">{error}</p>}
          <Button type="submit" size="lg" className="h-11" loading={busy} disabled={problems.length > 0}>{t('Create account')}</Button>
        </form>
      )}
    </AuthFrame>
  )
}

export function ForgotPage() {
  const { t } = useI18n()
  return (
    <AuthFrame title={t('Forgot your password?')} footer={<Link to="/login" className="font-medium text-text underline">{t('Back to sign in')}</Link>}>
      <p className="text-sm text-text-2">{t('Resetting a password by SMS is coming soon. Until then, write to us and we will help you get back in.')}</p>
      <a href="mailto:summit@saadhan.app?subject=Password%20reset" className="mt-4 flex h-11 items-center justify-center rounded-lg bg-primary text-sm font-medium text-primary-fg">summit@saadhan.app</a>
    </AuthFrame>
  )
}
